import { Button, Card, Flex, Select, Skeleton, Space, Typography } from 'antd';
import React, { useRef } from 'react';

import {
  AssessmentStepPager,
  type AssessmentFlowStep,
} from '@/components/assessment/AssessmentStepPager';
import { AssetContextModal } from '@/components/common/AssetContextModal';
import { CreateNoteModal } from '@/components/common/CreateNoteModal';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import {
  CrepeStakeholderTableField,
  type CrepeStakeholderTableFieldHandle,
} from '@/components/editor/CrepeStakeholderTableField';
import { AddAssetOptionsForm } from '@/components/layout/AddAssetOptionsForm';
import { BrowseAssetsDrawer } from '@/components/layout/BrowseAssetsDrawer';
import { LinkedDocumentList } from '@/components/layout/LinkedDocumentList';
import { useInterviewPrep } from '@/hooks/useInterviewPrep';

const { Text, Title } = Typography;

export interface InterviewPrepProps {
  clientLabel: string;
  projectAssessmentLabel: string;
  assessmentInstanceId: string;
  onRunGenerate: () => void;
  onNavigateAssessmentStep: (step: AssessmentFlowStep) => void;
  sharepointReady?: boolean | null;
  accessToken?: string | null;
  microsoftOboAssertion?: string | null;
  clientId?: string;
  projectId?: string;
  onMicrosoftReauth?: () => Promise<void> | void;
  onMicrosoftRefreshObo?: () => Promise<string | null>;
}

export const InterviewPrep: React.FC<InterviewPrepProps> = ({
  clientLabel: _clientLabel,
  projectAssessmentLabel: _projectAssessmentLabel,
  assessmentInstanceId,
  onRunGenerate,
  onNavigateAssessmentStep,
  sharepointReady = null,
  accessToken = null,
  microsoftOboAssertion = null,
  clientId,
  projectId,
  onMicrosoftReauth,
  onMicrosoftRefreshObo,
}) => {
  const engagementEditorRef = useRef<CrepeMarkdownFieldHandle | null>(null);
  const stakeholderRef = useRef<CrepeStakeholderTableFieldHandle | null>(null);

  const prep = useInterviewPrep({
    assessmentInstanceId,
    accessToken,
    clientId,
    projectId,
    onRunGenerate,
  });

  return (
    <div className="interview-prep-page" data-assessment-instance-id={assessmentInstanceId}>
      {/* HEADER — sticky */}
      <div className="interview-prep-page__header">
        <Flex wrap="wrap" gap="middle" align="flex-start" justify="space-between">
          <div className="interview-prep-page__hero-heading">
            <div className="interview-prep-page__step-badge" aria-hidden="true">
              1
            </div>
            <div className="interview-prep-page__hero-text">
              <Title level={3} className="interview-prep-page__hero-title">
                Interview Prep Guide Generator
              </Title>
              <Text type="secondary" className="interview-prep-page__hero-subtitle">
                Enter client context and stakeholder details. Blend AI will generate role-specific
                interview guides.
              </Text>
            </div>
          </div>
          <Space direction="vertical" align="end">
            <AssessmentStepPager current={1} onStepChange={onNavigateAssessmentStep} />
            {accessToken && clientId && projectId ? (
              <Button
                onClick={() =>
                  void prep.save(
                    engagementEditorRef.current?.getMarkdown() ?? '',
                    stakeholderRef.current?.getMarkdown() ?? '',
                  )
                }
                loading={prep.isSaving}
                disabled={prep.isLoading}
              >
                Save Assessment
              </Button>
            ) : null}
          </Space>
        </Flex>
      </div>

      {/* BODY — scrollable */}
      <div className="interview-prep-page__body">
        <Card title="Engagement Context & Priorities">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="create-note-page__title-row">
              <input
                id="interview-prep-assessment-name"
                aria-label="Assessment title"
                autoComplete="off"
                className="create-note-title-input"
                placeholder="Assessment"
                value={prep.assessmentName}
                onChange={(event) => prep.setAssessmentName(event.target.value)}
                disabled={prep.isLoading}
              />
            </div>
            <Text type="secondary" style={{ display: 'block' }}>
              Use the editor below for goals, constraints, and priorities (Markdown supported).
            </Text>
            {prep.isLoading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <CrepeMarkdownField
                ref={engagementEditorRef}
                bootKey={assessmentInstanceId}
                defaultMarkdown={prep.initialEngagementMarkdown}
                onMarkdownChange={() => undefined}
                minHeight={320}
              />
            )}
          </div>
        </Card>

        <Card title="Stakeholder List" size="small">
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Edit the table below (Markdown). Only text formatting is available in the toolbar; the
            table cannot be removed. Add rows using the table controls inside the editor.
          </Text>
          {prep.isLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <CrepeStakeholderTableField
              ref={stakeholderRef}
              bootKey={assessmentInstanceId}
              defaultMarkdown={prep.initialStakeholderMarkdown}
              onMarkdownChange={() => undefined}
              minHeight={150}
            />
          )}
        </Card>

        <Card title="Supporting Documents (optional)">
          <AddAssetOptionsForm
            resetIdentityKey={assessmentInstanceId}
            onCancel={() => undefined}
            sharepointReady={sharepointReady}
            accessToken={accessToken}
            microsoftOboAssertion={microsoftOboAssertion}
            clientId={clientId}
            projectId={projectId}
            onMicrosoftReauth={onMicrosoftReauth}
            onMicrosoftRefreshObo={onMicrosoftRefreshObo}
            onWordDocumentUploaded={prep.linkDocAndOpenModal}
            onCreateNoteNavigate={prep.openCreateNoteModal}
            onBrowseAssets={() => prep.setBrowseOpen(true)}
            intro={
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Add links, upload project documents (when SharePoint is configured), or draft and
                save a note inline (no navigation away). Same link and upload behavior as Add Asset.
                Optional for this step.
              </Text>
            }
          />
          {accessToken && clientId && projectId && (
            <div style={{ marginTop: 16 }}>
              <LinkedDocumentList
                cardIds={prep.linkedDocCardIds}
                clientId={clientId}
                projectId={projectId}
                accessToken={accessToken}
                onOpen={prep.openModalForCard}
                onRemove={prep.unlinkDoc}
              />
            </div>
          )}
        </Card>
      </div>

      {/* FOOTER — sticky */}
      <div className="interview-prep-page__footer">
        <Select
          aria-label="Interview guide skill"
          className="interview-prep-page__skill-select"
          loading={prep.skillsLoadLoading}
          disabled={prep.isLoading}
          value={prep.selectedGuideSkillValue ?? undefined}
          onChange={(v) => prep.setSelectedGuideSkillValue(String(v))}
          options={prep.guideSkillOptions}
          placeholder="Select interview guide skill"
        />
        <Button
          type="primary"
          size="large"
          onClick={() => prep.generate(engagementEditorRef.current?.getMarkdown() ?? '')}
          disabled={prep.isLoading || !prep.selectedGuideSkillValue}
        >
          Generate Interview Guide
        </Button>
      </div>

      {/* Asset browser drawer */}
      {accessToken && clientId && projectId && (
        <BrowseAssetsDrawer
          open={prep.browseOpen}
          onClose={() => prep.setBrowseOpen(false)}
          clientId={clientId}
          projectId={projectId}
          accessToken={accessToken}
          linkedCardIds={prep.linkedDocCardIds}
          onSelect={(cardId: string) => {
            prep.linkDocAndOpenModal(cardId);
            prep.setBrowseOpen(false);
          }}
        />
      )}

      {/* Linked asset context modal — shared with Step 2 */}
      {clientId && projectId ? (
        <AssetContextModal
          open={prep.cardModalOpen && prep.cardModalMode === 'context'}
          cardId={prep.cardModalId}
          accessToken={accessToken}
          clientId={clientId}
          projectId={projectId}
          onLink={prep.linkDocAndCloseModal}
          onClose={prep.closeCardModal}
        />
      ) : null}

      {/* Note creation modal — same layout as AssetContextModal, no inner card wrapper */}
      <CreateNoteModal
        open={prep.cardModalOpen && prep.cardModalMode === 'create-note'}
        assetSessionId={prep.cardModalId}
        accessToken={accessToken}
        clientId={clientId}
        projectId={projectId}
        onClose={prep.closeCardModal}
        onSaved={prep.linkDocAndOpenModal}
      />
    </div>
  );
};

export default InterviewPrep;
